import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { IoMdArrowBack } from "react-icons/io";
import { FiShoppingBag, FiMapPin, FiInfo, FiTruck, FiBox, FiCheckCircle } from "react-icons/fi";

const lagosLGAs = [
  "Agege", "Ajeromi-Ifelodun", "Alimosho", "Amuwo-Odofin", "Apapa", "Badagry",
  "Epe", "Eti-Osa", "Ibeju-Lekki", "Ifako-Ijaiye", "Ikeja", "Ikorodu", "Kosofe",
  "Lagos Island", "Lagos Mainland", "Mushin", "Ojo", "Oshodi-Isolo",
  "Shomolu", "Surulere"
];

const CreateOrderPage = () => {
  const navigate = useNavigate();
  const location = useLocation(); 
  const lgaRef = useRef(null);

  const sellerId = location.state?.sellerId;

  useEffect(() => {
    if (!sellerId) navigate(-1);
  }, [sellerId, navigate]);

  const [form, setForm] = useState({
    item: "",
    description: "",
    quantity: 1,
    amount: "",
    deliveryAddress: "",
    sellerId: sellerId || "",
    lga: "" 
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const orderData = {
      ...form,
      quantity: Number(form.quantity),
      amount: Number(form.amount)
    };
    navigate("/confirm-payments", { state: { order: orderData } });
  };

  return (
    <div className="bg-gray-50 min-h-screen pb-12">
      {/* 🔝 Sticky Header */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b px-4 py-4 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-full transition text-gray-700 flex items-center gap-1"
        >
          <IoMdArrowBack size={24} />
          <span className="text-[10px] font-black uppercase tracking-widest">Back</span>
        </button>
        <h1 className="text-lg font-black text-gray-900 tracking-tight pr-10">Checkout</h1>
        <div />
      </div>

      <div className="max-w-xl mx-auto px-6 pt-8">
        
        {/* 🚚 Delivery Step Banner */}
        <div className="bg-teal-900 rounded-[2.5rem] p-6 mb-8 text-white shadow-xl shadow-teal-100 overflow-hidden relative">
          <div className="absolute right-[-10%] top-[-20%] w-32 h-32 bg-white/10 rounded-full blur-3xl"></div>
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-4">
               <div className="p-2 bg-teal-800 rounded-xl"><FiTruck className="text-teal-100" /></div>
               <h2 className="text-sm font-black uppercase tracking-widest">How OurASAP Works</h2>
            </div>
            <div className="space-y-3">
               {[
                 "We contact the seller instantly",
                 "We collect the item from their location",
                 "Fast delivery to your doorstep"
               ].map((text, i) => (
                 <div key={i} className="flex items-center gap-3 text-xs font-bold text-teal-50">
                   <FiCheckCircle className="text-teal-400 shrink-0" /> {text}
                 </div>
               ))}
            </div>
          </div>
        </div>

        {/* 📝 Order Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white p-8 rounded-[2.5rem] shadow-xl shadow-gray-200/50 border border-gray-100">
            <h3 className="text-xl font-black text-gray-900 mb-6 flex items-center gap-2">
              <FiBox className="text-orange-600" /> Item Details
            </h3>

            <div className="space-y-5">
              {/* Item Name */}
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-2">Item Name</label>
                <input
                  type="text"
                  name="item"
                  value={form.item}
                  onChange={handleChange}
                  required
                  placeholder="What are you buying?"
                  className="w-full bg-gray-50 border-2 border-gray-100 focus:border-orange-500 focus:bg-white rounded-2xl py-4 px-5 font-bold text-gray-900 transition-all outline-none"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-2">Description (Optional)</label>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  rows="2"
                  placeholder="Color, size, or specific notes..."
                  className="w-full bg-gray-50 border-2 border-gray-100 focus:border-orange-500 focus:bg-white rounded-2xl py-4 px-5 font-bold text-gray-900 transition-all outline-none resize-none"
                />
              </div>

              {/* Quantity & Price */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-2">Quantity</label>
                  <input
                    type="number"
                    name="quantity"
                    value={form.quantity}
                    onChange={handleChange}
                    min="1"
                    required
                    className="w-full bg-gray-50 border-2 border-gray-100 focus:border-orange-500 focus:bg-white rounded-2xl py-4 px-5 font-bold text-gray-900 transition-all outline-none"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-2">Price (₦)</label>
                  <input
                    type="number"
                    name="amount"
                    value={form.amount}
                    onChange={handleChange}
                    required
                    placeholder="0.00"
                    className="w-full bg-gray-50 border-2 border-gray-100 focus:border-orange-500 focus:bg-white rounded-2xl py-4 px-5 font-bold text-gray-900 transition-all outline-none"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-8 rounded-[2.5rem] shadow-xl shadow-gray-200/50 border border-gray-100">
            <h3 className="text-xl font-black text-gray-900 mb-6 flex items-center gap-2">
              <FiMapPin className="text-orange-600" /> Shipping Info
            </h3>

            <div className="space-y-5">
              {/* LGA Dropdown */}
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-2">LGA (Lagos Only)</label>
                <select
                  ref={lgaRef}
                  name="lga"
                  value={form.lga}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-50 border-2 border-gray-100 focus:border-orange-500 focus:bg-white rounded-2xl py-4 px-5 font-bold text-gray-900 transition-all outline-none appearance-none cursor-pointer"
                >
                  <option value="">Select Local Government</option>
                  {lagosLGAs.map((lga) => (
                    <option key={lga} value={lga}>{lga}</option>
                  ))}
                </select>
              </div>

              {/* Delivery Address */}
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-2">Full Address</label>
                <input
                  type="text"
                  name="deliveryAddress"
                  value={form.deliveryAddress}
                  onChange={handleChange}
                  required
                  placeholder="Street name, house number..."
                  className="w-full bg-gray-50 border-2 border-gray-100 focus:border-orange-500 focus:bg-white rounded-2xl py-4 px-5 font-bold text-gray-900 transition-all outline-none"
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full py-5 bg-gray-900 hover:bg-black text-white rounded-[1.5rem] font-black uppercase tracking-[0.2em] text-sm transition-all shadow-xl shadow-gray-300 active:scale-95 flex items-center justify-center gap-3"
          >
            <FiShoppingBag size={18} />
            Place Order
          </button>
        </form>

        <p className="text-center mt-10 text-[10px] font-black text-gray-300 uppercase tracking-widest">
          Secure Checkout • Doorstep Delivery • OurASAP
        </p>
      </div>
    </div>
  );
};

export default CreateOrderPage;